import { MouseEvent, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import { NodeProps, Position } from 'reactflow';
import { CHILD_NODE_PADDING, GRAPH_CHILD_NODE_HEIGHT } from './constants';
import { ReactFlowNodeData } from './types';
import { useLineageGraphContext } from './LineageGraphContext';
import { GraphNodeCompactMenu } from './GraphNodeCompact';
import { CompactExpandButton, CompactPanelRow } from './GraphNodeCompactParts';
import GraphNodeHandle from './GraphNodeHandle';
import useCompactPanelBorderColor from './hooks/useCompactPanelBorderColor';
import {
  getFilterHighlightBackground,
  isFilterMatch,
} from './utils/lineageDisplaySettings';
import { getGraphNodeChildrenState } from './utils/lineageGraphUtils';

const GraphNodeCompactRow = (node: NodeProps<ReactFlowNodeData>) => {
  const { id, data } = node;
  const theme = useTheme();
  const { highlightedNode, isControlled } = useLineageGraphContext();
  const borderColor = useCompactPanelBorderColor(data.parentNode?.id);

  const rowRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const handleMenuOpen = (e: MouseEvent) => {
    if (isControlled) return;
    e.stopPropagation();
    e.preventDefault();
    setMenuPos({ left: e.clientX, top: e.clientY });
  };

  const { isExpandable, showChildrenSection, isVisuallyExpanded } =
    getGraphNodeChildrenState(data);
  const isHighlighted = highlightedNode?.id === id;
  const isOnHighlightPath = isHighlighted || !!data.isOnHighlightPath;

  return (
    <CompactPanelRow
      rowRef={rowRef}
      width={data.width + CHILD_NODE_PADDING * 2}
      borderColor={borderColor}
      label={data.metadata.label ?? ''}
      isLast={!!data.isLastCompactRow}
      isHighlighted={isHighlighted}
      isOnHighlightPath={isOnHighlightPath}
      filterBackground={
        isFilterMatch(data)
          ? getFilterHighlightBackground(theme.palette)
          : undefined
      }
      onContextMenu={handleMenuOpen}
    >
      <GraphNodeHandle
        type="target"
        position={Position.Left}
        $isHighlighted={isOnHighlightPath}
        $nodeHeight={GRAPH_CHILD_NODE_HEIGHT}
      />
      {showChildrenSection && isExpandable && (
        <CompactExpandButton
          size={24}
          isLoading={!!data.isColumnsLoading}
          isExpanded={isVisuallyExpanded}
        />
      )}
      <GraphNodeHandle
        type="source"
        position={Position.Right}
        $isHighlighted={isOnHighlightPath}
        $nodeHeight={GRAPH_CHILD_NODE_HEIGHT}
      />

      {menuPos && (
        <GraphNodeCompactMenu
          node={node}
          pos={menuPos}
          onClose={() => setMenuPos(null)}
          onHighlightNodeClick={() => rowRef.current?.click()}
        />
      )}
    </CompactPanelRow>
  );
};

export default GraphNodeCompactRow;
